import { createContext, useContext, useReducer, useEffect, useRef, ReactNode } from 'react'
import {
  User as FirebaseUser,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth'
import { configureCommunityAdmin } from '@metabus/community-admin'
import { auth } from '@/lib/firebase'
import {
  clearActiveTenant,
  getActiveTenant,
  initActiveTenant,
  switchActiveTenant,
} from '@/lib/api/activeTenant'
import type { AuthContextType, AuthState, AuthUser } from '@/types/auth'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

/**
 * Configure the shared community-admin runtime once, at module load.
 *
 * This app uses ONE thing from the package: the confirm-your-email screen and
 * its resend call, which posts to the dialer as the signed-in user. That route
 * is authed, so the tenant comes from the bearer token.
 *
 * `tenantId` is the path segment for the package's AUTHLESS public routes. This
 * app never calls those - its tenant is resolved from /api/me, and at the moment
 * the confirmation screen shows, that call has not succeeded. Left empty on
 * purpose.
 */
configureCommunityAdmin({
  apiBase: API_BASE_URL,
  tenantId: '',
  getAuthToken: async () => {
    const current = auth.currentUser
    return current ? current.getIdToken() : null
  },
})

const QA_BYPASS = import.meta.env.VITE_QA_BYPASS === 'true'
const QA_MOCK_USER: AuthUser = {
  user_id: 'qa-user',
  email: 'qa@buildgravity.com',
  name: 'QA User',
  role: 'admin',
  permissions: ['read', 'write', 'admin'],
  tenant_id: 'buildgravity',
  tenant: { id: 'buildgravity', name: 'BuildGravity' },
  available_tenants: [],
  modules: { sales: true, sales_dialer: true },
  is_active: true,
  is_admin: true,
  is_super_admin: false,
  last_login: new Date().toISOString(),
}

// Auth reducer
type AuthAction =
  | { type: 'AUTH_LOADING' }
  | { type: 'AUTH_SUCCESS'; payload: AuthUser }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_NEEDS_VERIFICATION'; payload: string | null }
  | { type: 'AUTH_SIGNOUT' }
  | { type: 'CLEAR_ERROR' }

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_LOADING':
      return { ...state, isLoading: true, error: null }
    case 'AUTH_SUCCESS':
      return {
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        needsEmailVerification: false,
        unverifiedEmail: null,
      }
    case 'AUTH_ERROR':
      return {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        needsEmailVerification: false,
        unverifiedEmail: null,
      }
    case 'AUTH_NEEDS_VERIFICATION':
      return {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        // Not an error state. The confirmation screen carries its own words, so
        // showing the denial text alongside it would only contradict them.
        error: null,
        needsEmailVerification: true,
        unverifiedEmail: action.payload,
      }
    case 'AUTH_SIGNOUT':
      return {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        needsEmailVerification: false,
        unverifiedEmail: null,
      }
    case 'CLEAR_ERROR':
      return { ...state, error: null }
    default:
      return state
  }
}

const initialState: AuthState = QA_BYPASS
  ? {
      user: QA_MOCK_USER,
      isAuthenticated: true,
      isLoading: false,
      error: null,
      needsEmailVerification: false,
      unverifiedEmail: null,
    }
  : {
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      needsEmailVerification: false,
      unverifiedEmail: null,
    }

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const useHasDialer = () => {
  const { user } = useAuth()
  return user?.modules?.sales_dialer === true
}

export const useCanWrite = () => {
  const { user } = useAuth()
  return user?.modules?.sales === true
}

export const useIsViewer = () => {
  const { user } = useAuth()
  return user?.role === 'viewer'
}

/** SPEC E4 R1 — true when the authed user is a read-only CLIENT-portal user.
 * Drives the PortalRoute guard + the operator-route redirect in App.tsx. */
export const useIsClientRole = () => {
  const { user } = useAuth()
  return user?.role === 'client'
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, dispatch] = useReducer(authReducer, initialState)
  // The signed-in operator's uid: the key their saved tenant choice lives under.
  const uidRef = useRef<string | null>(null)

  // Fetch user profile from backend
  const fetchUserProfile = async (firebaseUser: FirebaseUser): Promise<AuthUser | null> => {
    try {
      const token = await firebaseUser.getIdToken()
      const response = await fetch(`${API_BASE_URL}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      // 400 as well: the dialer rejects a saved value that is not shaped like a
      // tenant id before anything else, and that choice is just as unusable.
      if ((response.status === 403 || response.status === 400) && getActiveTenant()) {
        // The tenant this browser last worked in is no longer allowed: the
        // membership was removed after it was chosen. Forget it and load the
        // operator's default instead of failing sign-in. The retry carries no
        // X-Act-As-Tenant, so this runs at most once.
        clearActiveTenant(firebaseUser.uid)
        return fetchUserProfile(firebaseUser)
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch user profile: ${response.statusText}`)
      }

      const data = await response.json()

      // Backend returns { user: {...}, timestamp: ... }
      const user = data.user

      // Transform backend response to AuthUser format
      const authUser: AuthUser = {
        user_id: user.user_id,
        email: user.email,
        name: user.name,
        role: user.role,
        permissions: user.permissions || [],
        tenant_id: user.tenant_id,
        tenant: user.tenant || { id: user.tenant_id, name: user.tenant_name || user.tenant_id },
        available_tenants: user.available_tenants || [],
        modules: user.modules,
        is_active: user.is_active ?? true,
        is_admin: user.is_admin ?? false,
        is_client: user.is_client ?? false,
        is_super_admin: user.is_super_admin ?? false,
        last_login: data.timestamp || new Date().toISOString(),
        // SPEC E4 — portal fields (present only for CLIENT-role users).
        portal_views: user.portal_views ?? null,
        enabled_views: user.enabled_views ?? undefined,
        client_label: user.client_label ?? null,
        branding: user.branding ?? undefined,
      }

      return authUser
    } catch (error) {
      console.error('Error fetching user profile:', error)
      throw error
    }
  }

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Restore this operator's saved tenant BEFORE the first /api/me, so the
        // profile, and every request after it, are scoped to it.
        uidRef.current = firebaseUser.uid
        initActiveTenant(firebaseUser.uid)
        try {
          dispatch({ type: 'AUTH_LOADING' })
          const authUser = await fetchUserProfile(firebaseUser)
          if (authUser) {
            dispatch({ type: 'AUTH_SUCCESS', payload: authUser })
          } else {
            dispatch({ type: 'AUTH_ERROR', payload: 'Failed to load user profile' })
          }
        } catch (error) {
          // The dialer answers an unconfirmed address with the same opaque 403 it
          // gives a genuinely unauthorized one, deliberately, so a caller cannot
          // probe which addresses are registered. It does not have to tell us:
          // the signed-in user's own token already carries `emailVerified`, and
          // that claim is about the caller themselves, so reading it here reveals
          // nothing and asks the server for nothing.
          //
          // The session is deliberately NOT torn down here. Signing out would
          // discard the very token the resend call needs, leaving the user with
          // no way to reach the one action that fixes their problem.
          if (firebaseUser.emailVerified === false) {
            dispatch({
              type: 'AUTH_NEEDS_VERIFICATION',
              payload: firebaseUser.email,
            })
            return
          }
          dispatch({
            type: 'AUTH_ERROR',
            payload: error instanceof Error ? error.message : 'Authentication failed'
          })
        }
      } else {
        uidRef.current = null
        initActiveTenant(null)
        dispatch({ type: 'AUTH_SIGNOUT' })
      }
    })

    return () => unsubscribe()
  }, [])

  // Sign in with email/password
  const signInWithEmail = async (email: string, password: string) => {
    try {
      dispatch({ type: 'AUTH_LOADING' })
      await signInWithEmailAndPassword(auth, email, password)
      // onAuthStateChanged will handle the rest
    } catch (error) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: error instanceof Error ? error.message : 'Sign in failed',
      })
      throw error
    }
  }

  // Sign in with Google
  const signIn = async () => {
    try {
      dispatch({ type: 'AUTH_LOADING' })
      const provider = new GoogleAuthProvider()
      await signInWithPopup(auth, provider)
      // onAuthStateChanged will handle the rest
    } catch (error) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: error instanceof Error ? error.message : 'Sign in failed',
      })
      throw error
    }
  }

  // Create user with email/password
  const createUserWithEmail = async (email: string, password: string) => {
    try {
      dispatch({ type: 'AUTH_LOADING' })
      await createUserWithEmailAndPassword(auth, email, password)
      // onAuthStateChanged will handle the rest
    } catch (error) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: error instanceof Error ? error.message : 'Account creation failed',
      })
      throw error
    }
  }

  // Sign out
  const signOut = async () => {
    try {
      await firebaseSignOut(auth)
      dispatch({ type: 'AUTH_SIGNOUT' })
    } catch (error) {
      dispatch({
        type: 'AUTH_ERROR',
        payload: error instanceof Error ? error.message : 'Sign out failed',
      })
      throw error
    }
  }

  // Switch tenant: scope THIS browser session to another of the operator's
  // tenants. It rides on every dialer request as X-Act-As-Tenant (see
  // lib/api/activeTenant.ts) and the server re-validates membership each time.
  // This no longer POSTs /api/switch-tenant, which rewrote the operator's stored
  // default_tenant and so changed every other open tab, device and background
  // job signed in as the same person.
  const switchTenant = async (tenantId: string) => {
    const uid = uidRef.current
    if (!uid) {
      throw new Error('Not authenticated')
    }
    if (!state.user?.available_tenants?.some((t) => t.tenant_id === tenantId)) {
      throw new Error(`You do not have access to tenant '${tenantId}'`)
    }
    switchActiveTenant(uid, tenantId)
  }

  // Clear error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' })
  }

  const value: AuthContextType = {
    ...state,
    signIn,
    signInWithEmail,
    createUserWithEmail,
    signOut,
    switchTenant,
    clearError,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
